import {useState, useCallback, useEffect, ReactNode} from 'react';
import {SessionMeta, InputMode} from '../lib/types';
import {activateModelSession, createModelSession, loadSessions, saveSessions, deleteSession} from '../lib/sessions';
import {ModelId, OCR_MODEL_ID} from '../lib/config';
import {modelChatPath, modelIdFromUrl} from '../lib/modelRoute';
import {sessionHasOcrDraft} from '../lib/ocrDrafts';
import {brand, space, fontFamily} from '../brand/theme';
import {btnPrimaryStyle} from '../brand/styles';
import Sidebar from './Sidebar';
import {AccountTab} from '../features/AccountTab';
import {BatchMode} from '../features/BatchMode';

interface KeyModal {
  draft: string;
  setDraft: (v: string) => void;
  onSubmit: () => void;
  onDefault: () => void;
  onClose: () => void;
  validating?: boolean;
  error?: string;
}

interface Props {
  renderView: (session: SessionMeta, onUpdate: (s: SessionMeta) => void, onDirtyChange: (dirty: boolean) => void) => ReactNode;
  userId: string;
  requestedModelId?: ModelId | null;
  apiKey?: string;
  onLogout?: () => void;
  keyModal?: KeyModal;
}

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= 768);
    handler(); // re-check on mount
    window.addEventListener('resize', handler);
    window.visualViewport?.addEventListener('resize', handler);
    return () => { window.removeEventListener('resize', handler); window.visualViewport?.removeEventListener('resize', handler); };
  }, []);
  return mobile;
}

function loadInitialState(requestedModelId?: ModelId | null) {
  const sessions = loadSessions();
  const saved = localStorage.getItem('lapathoniia_active_session');
  const activeId = saved && sessions.some(session => session.id === saved) ? saved : sessions[0]?.id || null;
  if (!requestedModelId) return {sessions, activeId};
  const activated = activateModelSession(sessions, activeId, requestedModelId);
  if (activated.created) {
    saveSessions(activated.sessions);
    localStorage.setItem('lapathoniia_active_session', activated.activeId);
  }
  return {sessions: activated.sessions, activeId: activated.activeId};
}

export default function ChatLayout({renderView, userId, requestedModelId, apiKey, onLogout, keyModal}: Props) {
  const [initialState] = useState(() => loadInitialState(requestedModelId));
  const [sessions, setSessions] = useState<SessionMeta[]>(initialState.sessions);
  const [activeId, setActiveId] = useState<string | null>(() => {
    const saved = localStorage.getItem('lapathoniia_active_session');
    if (saved && sessions.find(s => s.id === saved)) return saved;
    return initialState.activeId;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [dirtySessionIds, setDirtySessionIds] = useState<Set<string>>(() => new Set(
    sessions.filter(session => sessionHasOcrDraft(userId, session.id)).map(session => session.id),
  ));
  const isMobile = useIsMobile();

  useEffect(() => { if (activeId) localStorage.setItem('lapathoniia_active_session', activeId); }, [activeId]);

  useEffect(() => {
    const activeSession = sessions.find(session => session.id === activeId);
    const isOcrScreen = activeSession?.mode === 'ocr' || activeSession?.modelId === OCR_MODEL_ID;
    const currentRouteIsOcr = modelIdFromUrl(window.location) === OCR_MODEL_ID;
    const nextPath = isOcrScreen ? modelChatPath(OCR_MODEL_ID) : currentRouteIsOcr ? '/' : null;
    if (!nextPath || `${window.location.pathname}${window.location.search}${window.location.hash}` === nextPath) return;
    window.history.replaceState(window.history.state, '', nextPath);
  }, [activeId, sessions]);

  const persist = (next: SessionMeta[]) => { setSessions(next); saveSessions(next); };

  const handleNew = (type: InputMode) => {
    const s = {...createModelSession(), type};
    persist([s, ...sessions]);
    setActiveId(s.id);
    setShowAccount(false);
    setShowBatch(false);
    if (isMobile) setSidebarOpen(false);
  };

  const handleDelete = (id: string) => {
    if (dirtySessionIds.has(id) || sessionHasOcrDraft(userId, id)) {
      setActiveId(id);
      return;
    }
    deleteSession(id);
    const next = sessions.filter(s => s.id !== id);
    setSessions(next);
    if (activeId === id) setActiveId(next[0]?.id || null);
  };

  const handleSelect = (id: string) => {
    setActiveId(id);
    setShowAccount(false);
    setShowBatch(false);
    if (isMobile) setSidebarOpen(false);
  };

  const handleUpdateSession = useCallback((updated: SessionMeta) => {
    setSessions(prev => {
      const next = prev.map(s => s.id === updated.id ? updated : s);
      saveSessions(next);
      return next;
    });
  }, []);

  const handleDirtyChange = useCallback((sessionId: string, dirty: boolean) => {
    setDirtySessionIds(current => {
      if (current.has(sessionId) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(sessionId); else next.delete(sessionId);
      return next;
    });
  }, []);

  const active = sessions.find(s => s.id === activeId);

  return (
    <div style={{display: 'flex', height: '100dvh', fontFamily, background: brand.bg, position: 'relative', overflow: 'hidden'}}>
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className={isMobile ? `sidebar-overlay${sidebarOpen ? ' open' : ''}` : ''}>
        <Sidebar sessions={sessions} activeId={activeId} onSelect={handleSelect} onNew={handleNew} onDelete={handleDelete} dirtySessionIds={dirtySessionIds} apiKey={apiKey} onLogout={onLogout} onShowAccount={() => { setShowAccount(true); setShowBatch(false); if (isMobile) setSidebarOpen(false); }} onShowBatch={() => { setShowBatch(true); setShowAccount(false); if (isMobile) setSidebarOpen(false); }} />
      </div>

      {/* Main content */}
      <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0}}>
        {/* Mobile header with hamburger */}
        {isMobile && (
          <div style={{display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: `1px solid ${brand.border}`, background: brand.bgCard, gap: '12px', zIndex: 100, flexShrink: 0, position: 'relative'}}>
            <button onClick={() => setSidebarOpen(true)} style={{border: 'none', background: 'none', color: brand.cream, fontSize: '24px', cursor: 'pointer', padding: '8px', lineHeight: 1, minWidth: '40px', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center'}} aria-label="Меню">☰</button>
            <a href="https://lapathoniia.top" target="_blank" rel="noopener noreferrer" style={{display:'flex',alignItems:'center'}}>
              <img src="/lapathoniia-logo.png" alt="Lapathoniia" height={28} style={{filter: 'drop-shadow(0 0 6px rgba(59,130,246,0.3))'}} />
            </a>
          </div>
        )}

        {showAccount ? (
          <div style={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: `${space(1.5)} ${space(2)}`, borderBottom: `1px solid ${brand.border}`, background: brand.bgCard, flexShrink: 0}}>
              <button onClick={() => setShowAccount(false)} style={{border: 'none', background: 'none', color: brand.creamMuted, fontSize: '20px', cursor: 'pointer', padding: '4px', lineHeight: 1}} title="Назад">←</button>
              <span style={{fontSize: '15px', fontWeight: 600, color: brand.cream}}>📊 Моє використання</span>
            </div>
            <div style={{flex: 1, overflow: 'auto'}}>
              <AccountTab apiKey={apiKey} />
            </div>
          </div>
        ) : showBatch ? (
          <div style={{flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: `${space(1.5)} ${space(2)}`, borderBottom: `1px solid ${brand.border}`, background: brand.bgCard, flexShrink: 0}}>
              <button onClick={() => setShowBatch(false)} style={{border: 'none', background: 'none', color: brand.creamMuted, fontSize: '20px', cursor: 'pointer', padding: '4px', lineHeight: 1}} title="Назад">←</button>
              <span style={{fontSize: '15px', fontWeight: 600, color: brand.cream}}>⚡ Пакетний режим</span>
              <span style={{fontSize: '11px', padding: '2px 7px', borderRadius: '10px', background: 'rgba(78,205,196,0.12)', color: brand.tealLight, border: `1px solid rgba(78,205,196,0.25)`, marginLeft: 2}}>40% дешевше</span>
            </div>
            <BatchMode apiKey={apiKey} />
          </div>
        ) : active ? renderView(active, handleUpdateSession, dirty => handleDirtyChange(active.id, dirty)) : (
          <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `url('/bg-pattern.svg') no-repeat center center / cover`}}>
            <div style={{textAlign: 'center', padding: '0 16px'}}>
              <div style={{fontSize: '56px', marginBottom: space(2)}}>🇺🇦</div>
              <h2 style={{margin: `0 0 ${space(1)}`, fontSize: '22px', color: brand.cream, fontWeight: 600}}>Lapathoniia</h2>
              <p style={{fontSize: '14px', marginBottom: space(3), color: brand.creamMuted}}>Почніть новий чат з українськими AI моделями</p>
              <button onClick={() => handleNew('free_form')} style={btnPrimaryStyle}>+ Новий чат</button>
            </div>
          </div>
        )}
      </div>

      {/* API Key Modal */}
      {keyModal && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center'}} onClick={keyModal.onClose}>
          <div style={{background: brand.bgCard, border: `1px solid ${brand.border}`, borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '90%'}} onClick={e => e.stopPropagation()}>
            <h3 style={{margin: '0 0 8px', color: brand.cream, fontSize: '16px'}}>🔑 API ключ</h3>
            <p style={{margin: '0 0 16px', color: brand.creamMuted, fontSize: '13px'}}>
              Введіть свій Lapathoniia API ключ для використання власного бюджету.{' '}
              <a href="https://lapathoniia.top/dashboard" target="_blank" rel="noopener noreferrer" style={{color: '#f4c542', textDecoration: 'underline'}}>Отримати ключ →</a>
            </p>
            <input
              type="password" value={keyModal.draft} onChange={e => { keyModal.setDraft(e.target.value); }}
              placeholder="sk-..." autoFocus
              disabled={keyModal.validating}
              style={{width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${keyModal.error ? brand.red : keyModal.draft.trim() ? 'rgba(244,197,66,0.4)' : brand.border}`, background: brand.bgMuted, color: brand.cream, fontSize: '14px', fontFamily, outline: 'none', boxSizing: 'border-box', transition: 'border-color 150ms', opacity: keyModal.validating ? 0.6 : 1}}
              onKeyDown={e => { if (e.key === 'Enter' && keyModal.draft.trim() && !keyModal.validating) keyModal.onSubmit(); }}
            />
            {/* Validation feedback */}
            {keyModal.error && (
              <div style={{marginTop: '8px', padding: '8px 10px', borderRadius: '6px', background: brand.errorMuted, border: `1px solid ${brand.red}`, fontSize: '12px', color: brand.red}}>
                ❌ {keyModal.error}
              </div>
            )}
            {!keyModal.error && keyModal.draft.trim().length > 8 && !keyModal.validating && (
              <div style={{marginTop: '8px', padding: '6px 10px', borderRadius: '6px', background: 'rgba(244,197,66,0.08)', border: '1px solid rgba(244,197,66,0.2)', fontSize: '11px', color: '#f4c542', fontFamily: 'monospace'}}>
                {keyModal.draft.trim().slice(0, 8)}···
              </div>
            )}
            {keyModal.validating && (
              <div style={{marginTop: '8px', padding: '6px 10px', borderRadius: '6px', background: 'rgba(78,205,196,0.08)', border: `1px solid rgba(78,205,196,0.2)`, fontSize: '12px', color: brand.tealLight}}>
                ⏳ Перевірка ключа…
              </div>
            )}
            <div style={{display: 'flex', gap: '8px', marginTop: '16px'}}>
              <button
                onClick={keyModal.onSubmit}
                disabled={!keyModal.draft.trim() || keyModal.validating}
                style={{flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: keyModal.draft.trim() && !keyModal.validating ? '#f4c542' : brand.border, color: keyModal.draft.trim() && !keyModal.validating ? '#071316' : brand.creamMuted, fontSize: '13px', fontWeight: 600, cursor: keyModal.draft.trim() && !keyModal.validating ? 'pointer' : 'not-allowed', fontFamily, transition: 'background 150ms'}}
              >
                {keyModal.validating ? 'Перевірка…' : 'Підключити ключ'}
              </button>
              <button onClick={keyModal.onDefault} disabled={keyModal.validating} style={{flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${brand.border}`, background: 'transparent', color: brand.creamMuted, fontSize: '13px', cursor: 'pointer', fontFamily, opacity: keyModal.validating ? 0.5 : 1}}>Демо-режим</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
